// hooks/useLocalPagination.ts

import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "convex/react";
import type { FunctionReference } from "convex/server";

// ============================================
// TIPOS
// ============================================

interface PaginationResponse<T> {
  page: T[];
  hasMore?: boolean;
  isDone?: boolean;
  continueCursor?: string;
  totalCount?: number;
  pagination?: {
    totalCount?: number;
    totalPages?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
}

// Campos estándar que tu backend espera
interface StandardPaginationFilters {
  status?: string;
  search?: string;
  targetDateFrom?: string;
  targetDateTo?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  numericRanges?: string;
}

interface UseLocalPaginationOptions<TFilters extends Record<string, any>> {
  queryFunction: FunctionReference<"query">;
  initialFilters?: TFilters;
  pageSize?: number;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  onFiltersChange?: (filters: TFilters) => void;
  onDataLoad?: (data: any) => void;
}

interface PaginationState<T, TFilters extends Record<string, any>> {
  // Data
  data: T[];
  isLoading: boolean;
  error: any;

  // Pagination info
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;

  // Filters
  filters: TFilters;

  // Actions - Pagination
  goToPage: (page: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;

  // Actions - Filters
  updateFilters: (newFilters: Partial<TFilters>) => void;
  setFilters: (filters: TFilters) => void;
  resetFilters: () => void;
  clearFilters: () => void;

  // Actions - Combined
  reset: () => void;

  // Metadata
  isEmpty: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
}

// ============================================
// HOOK PRINCIPAL
// ============================================

export function useLocalPagination<
  T = any,
  TFilters extends Record<string, any> = Record<string, any>,
>({
  queryFunction,
  initialFilters = {} as TFilters,
  pageSize = 20,
  initialPage = 1,
  onPageChange,
  onFiltersChange,
  onDataLoad,
}: UseLocalPaginationOptions<TFilters>): PaginationState<T, TFilters> {
  // ============================================
  // ESTADO
  // ============================================

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [filters, setFiltersState] = useState<TFilters>(initialFilters);

  // ============================================
  // QUERY ARGS (Memoized)
  // ============================================

  const queryArgs = useMemo(() => {
    // Limpiar valores undefined/null/empty de filtros
    const cleanFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => {
        if (value === undefined || value === null || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );

    return {
      ...cleanFilters,
      pageNumber: currentPage - 1, // 0-indexed
      targetMaxRows: pageSize, // Tu backend usa targetMaxRows
      includeTotalCount: true, // Para obtener totalCount
    };
  }, [filters, currentPage, pageSize]);

  // ============================================
  // QUERY
  // ============================================

  const data = useQuery(queryFunction, queryArgs) as
    | PaginationResponse<T>
    | undefined;

  // ============================================
  // DERIVED STATE
  // ============================================

  const totalCount = useMemo(() => {
    return data?.totalCount || data?.pagination?.totalCount || 0;
  }, [data]);

  const totalPages = useMemo(() => {
    if (data?.pagination?.totalPages) {
      return data.pagination.totalPages;
    }
    return Math.ceil(totalCount / pageSize);
  }, [totalCount, pageSize, data?.pagination?.totalPages]);

  const hasNextPage = useMemo(() => {
    if (data?.pagination?.hasNextPage !== undefined) {
      return data.pagination.hasNextPage;
    }
    if (data?.hasMore !== undefined) {
      return data.hasMore;
    }
    return currentPage < totalPages;
  }, [currentPage, totalPages, data]);

  const hasPreviousPage = useMemo(() => {
    if (data?.pagination?.hasPrevPage !== undefined) {
      return data.pagination.hasPrevPage;
    }
    return currentPage > 1;
  }, [currentPage, data]);

  const isEmpty = useMemo(() => {
    return data?.page?.length === 0;
  }, [data]);

  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  // ============================================
  // CALLBACKS
  // ============================================

  // Callback cuando cambia la data
  useEffect(() => {
    if (data && onDataLoad) {
      onDataLoad(data);
    }
  }, [data, onDataLoad]);

  // ============================================
  // ACTIONS - PAGINATION
  // ============================================

  const goToPage = useCallback(
    (page: number) => {
      const targetPage = Math.max(1, Math.min(page, totalPages || 1));
      setCurrentPage(targetPage);
      onPageChange?.(targetPage);
    },
    [totalPages, onPageChange],
  );

  const nextPage = useCallback(() => {
    if (hasNextPage) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, hasNextPage, goToPage]);

  const previousPage = useCallback(() => {
    if (hasPreviousPage) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, hasPreviousPage, goToPage]);

  const goToFirstPage = useCallback(() => {
    goToPage(1);
  }, [goToPage]);

  const goToLastPage = useCallback(() => {
    if (totalPages > 0) {
      goToPage(totalPages);
    }
  }, [totalPages, goToPage]);

  // ============================================
  // ACTIONS - FILTERS
  // ============================================

  const updateFilters = useCallback(
    (newFilters: Partial<TFilters>) => {
      setFiltersState((prev) => {
        const updated = { ...prev, ...newFilters };
        onFiltersChange?.(updated);
        return updated;
      });
      // Reset a página 1 cuando cambian los filtros
      setCurrentPage(1);
    },
    [onFiltersChange],
  );

  const setFilters = useCallback(
    (newFilters: TFilters) => {
      setFiltersState(newFilters);
      onFiltersChange?.(newFilters);
      setCurrentPage(1);
    },
    [onFiltersChange],
  );

  const resetFilters = useCallback(() => {
    setFiltersState(initialFilters);
    onFiltersChange?.(initialFilters);
    setCurrentPage(1);
  }, [initialFilters, onFiltersChange]);

  const clearFilters = useCallback(() => {
    setFiltersState({} as TFilters);
    onFiltersChange?.({} as TFilters);
    setCurrentPage(1);
  }, [onFiltersChange]);

  // ============================================
  // ACTIONS - COMBINED
  // ============================================

  const reset = useCallback(() => {
    setCurrentPage(initialPage);
    setFiltersState(initialFilters);
  }, [initialPage, initialFilters]);

  // ============================================
  // RETURN
  // ============================================

  return {
    // Data
    data: data?.page ?? [],
    isLoading: data === undefined,
    error: null,

    // Pagination info
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage,
    hasPreviousPage,

    // Filters
    filters,

    // Actions - Pagination
    goToPage,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,

    // Actions - Filters
    updateFilters,
    setFilters,
    resetFilters,
    clearFilters,

    // Actions - Combined
    reset,

    // Metadata
    isEmpty,
    isFirstPage,
    isLastPage,
  };
}

// ============================================
// VARIANTE SIMPLE (Sin Filtros)
// ============================================

interface UseSimplePaginationOptions {
  queryFunction: FunctionReference<"query">;
  queryArgs?: Record<string, any>;
  pageSize?: number;
  initialPage?: number;
  onPageChange?: (page: number) => void;
}

export function useSimplePagination<T = any>({
  queryFunction,
  queryArgs = {},
  pageSize = 20,
  initialPage = 1,
  onPageChange,
}: UseSimplePaginationOptions) {
  const [currentPage, setCurrentPage] = useState(initialPage);

  const finalQueryArgs = useMemo(
    () => ({
      ...queryArgs,
      pageNumber: currentPage - 1,
      targetMaxRows: pageSize, // Tu backend usa targetMaxRows
      includeTotalCount: true,
    }),
    [queryArgs, currentPage, pageSize],
  );

  const data = useQuery(queryFunction, finalQueryArgs) as
    | PaginationResponse<T>
    | undefined;

  const totalCount = data?.totalCount || data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;

  const goToPage = useCallback(
    (page: number) => {
      const targetPage = Math.max(1, Math.min(page, totalPages || 1));
      setCurrentPage(targetPage);
      onPageChange?.(targetPage);
    },
    [totalPages, onPageChange],
  );

  const nextPage = useCallback(() => {
    if (hasNextPage) goToPage(currentPage + 1);
  }, [currentPage, hasNextPage, goToPage]);

  const previousPage = useCallback(() => {
    if (hasPreviousPage) goToPage(currentPage - 1);
  }, [currentPage, hasPreviousPage, goToPage]);

  return {
    data: data?.page ?? [],
    isLoading: data === undefined,
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    nextPage,
    previousPage,
    isEmpty: data?.page?.length === 0,
    isFirstPage: currentPage === 1,
    isLastPage: currentPage === totalPages,
  };
}

// ============================================
// HELPERS PARA FILTROS COMUNES
// ============================================

/**
 * Helper para crear filtros de rango de fechas
 */
export function createDateRangeFilter(
  startDate?: Date | number | string,
  endDate?: Date | number | string,
) {
  if (!startDate && !endDate) return {};

  return {
    targetDateFrom: startDate ? new Date(startDate).toISOString() : undefined,
    targetDateTo: endDate ? new Date(endDate).toISOString() : undefined,
  };
}

/**
 * Helper para crear filtros de rangos numéricos
 * Formato esperado: "fieldName:min:max,fieldName2:min:max"
 */
export function createNumericRangeFilter(
  ranges: Record<string, { min?: number; max?: number }>,
) {
  const rangeStrings = Object.entries(ranges)
    .filter(([_, range]) => range.min !== undefined || range.max !== undefined)
    .map(([field, range]) => {
      const min = range.min ?? "";
      const max = range.max ?? "";
      return `${field}:${min}:${max}`;
    });

  return rangeStrings.length > 0
    ? { numericRanges: rangeStrings.join(",") }
    : {};
}

/**
 * Hook con helpers de filtros comunes incluidos
 */
export function useStandardPagination<T = any>(
  options: UseSimplePaginationOptions & {
    initialStatus?: string;
    initialSearch?: string;
    initialDateRange?: { from?: Date; to?: Date };
    initialSortBy?: string;
    initialSortDir?: "asc" | "desc";
  },
) {
  const {
    initialStatus,
    initialSearch,
    initialDateRange,
    initialSortBy,
    initialSortDir,
    ...paginationOptions
  } = options;

  // Estado de filtros estándar
  const [status, setStatus] = useState(initialStatus);
  const [search, setSearch] = useState(initialSearch || "");
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [sortDir, setSortDir] = useState<"asc" | "desc" | undefined>(
    initialSortDir,
  );

  // Combinar filtros con queryArgs originales
  const queryArgs = useMemo(() => {
    const filters: Record<string, any> = {
      ...paginationOptions.queryArgs,
    };

    if (status) filters.status = status;
    if (search) filters.search = search;
    if (sortBy) filters.sortBy = sortBy;
    if (sortDir) filters.sortDir = sortDir;

    // Agregar date range
    if (dateRange) {
      Object.assign(
        filters,
        createDateRangeFilter(dateRange.from, dateRange.to),
      );
    }

    return filters;
  }, [paginationOptions.queryArgs, status, search, sortBy, sortDir, dateRange]);

  const pagination = useSimplePagination<T>({
    ...paginationOptions,
    queryArgs,
  });

  const resetFilters = useCallback(() => {
    setStatus(initialStatus);
    setSearch(initialSearch || "");
    setDateRange(initialDateRange);
    setSortBy(initialSortBy);
    setSortDir(initialSortDir);
  }, [
    initialStatus,
    initialSearch,
    initialDateRange,
    initialSortBy,
    initialSortDir,
  ]);

  return {
    ...pagination,
    // Filtros
    filters: {
      status,
      search,
      dateRange,
      sortBy,
      sortDir,
    },
    // Setters de filtros
    setStatus,
    setSearch,
    setDateRange,
    setSortBy,
    setSortDir,
    resetFilters,
  };
}
